import axios from "axios";
import AuthService from "./auth.service";
import {APP_DOMAIN_PREFIX, AUTH_API_URL} from "../environment";
import {clearAuthTokens, getAuthTokens, setAuthTokens} from "./utils";

let currentAccessToken = null;

// Tracks the current access token in memory; the request interceptor below
// reads this to decide whether to attach it. Deliberately NOT set via
// axios.defaults.headers.common — that would attach it to every axios call
// app-wide, including third-party calls this app makes directly (e.g.
// geolocation-db.com for phone-country detection), several of which reject
// unrecognized headers outright via CORS preflight.
export const applyAuthHeader = (accessToken) => {
  currentAccessToken = accessToken || null;
};

let refreshInFlight = null;

// Access tokens expire in 15 minutes (see auth-service .env). Without this,
// every API call would start silently failing 15 minutes into a session.
// On any 401 from OUR backends (not a login attempt itself), try exactly
// once to refresh via auth-service and replay the original request; if the
// refresh token is also dead, clear everything and send the user to sign in.
export const setupAuthInterceptors = () => {
  const tokens = getAuthTokens();
  applyAuthHeader(tokens?.accessToken);

  axios.interceptors.request.use((config) => {
    const isOwnDomain = typeof config.url === "string" && (
      config.url.startsWith(APP_DOMAIN_PREFIX) || config.url.startsWith("/")
    );
    if (isOwnDomain && currentAccessToken && !config.headers?.Authorization) {
      config.headers = {...config.headers, Authorization: `Bearer ${currentAccessToken}`};
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const {config, response} = error;
      const isAuthCall = config?.url?.includes(`${AUTH_API_URL}/auth/`);

      if (response?.status !== 401 || isAuthCall || config?._retriedAfterRefresh) {
        return Promise.reject(error);
      }

      const currentTokens = getAuthTokens();
      if (!currentTokens?.refreshToken) {
        return Promise.reject(error);
      }

      try {
        refreshInFlight = refreshInFlight || AuthService.refresh(currentTokens.refreshToken);
        const refreshResponse = await refreshInFlight;
        const {accessToken, refreshToken} = refreshResponse.data;
        setAuthTokens({accessToken, refreshToken});
        applyAuthHeader(accessToken);

        config._retriedAfterRefresh = true;
        config.headers = {...config.headers, Authorization: `Bearer ${accessToken}`};
        return axios(config);
      } catch (refreshError) {
        clearAuthTokens();
        applyAuthHeader(null);
        if (typeof window !== "undefined" && window.location.pathname !== "/signIn") {
          window.location.href = "/signIn";
        }
        return Promise.reject(error);
      } finally {
        refreshInFlight = null;
      }
    }
  );
};
